import type { FolderPaths, ISbStoriesParamsGeneric } from "~/types/Storyblok";

export type StoryblokRequestConfigOptions<
  TFolderPath extends FolderPaths,
  C extends string = string,
> = {
  /**
   * The base folder for the content type you want to fetch from Storyblok.
   */
  folderPath: TFolderPath;
  /**
   * If true, the dynamic slugs from the current route will be appended to the base folder while generating the slug.
   */
  shouldAppendRouteSlugs?: boolean;
  slugParamName?: string;
  params?: Partial<ISbStoriesParamsGeneric<C>>;
  suspense?: boolean;
};

export const useIsInStoryblokPreviewMode = () => {
  const route = useRoute();
  const runtimeConfig = useRuntimeConfig();

  return computed(
    () =>
      route.query["_storyblok_tk[space_id]"] ===
      runtimeConfig.public.storyblokSpaceId,
  );
};

/**
 * This composable is used to generate the slug and params for a Storyblok request by taking into account platform-scoping, localisation, and preview mode support.
 */
const useStoryblokRequestConfig = <
  TFolderPath extends FolderPaths,
  C extends string = string,
>({
  folderPath,
  shouldAppendRouteSlugs = true,
  slugParamName = "slug",
  params: baseParams,
}: StoryblokRequestConfigOptions<TFolderPath, C>) => {
  const route = useRoute();
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const slug = computed(() => {
    const contentFolder = `${platformId.value}/${folderPath}` as const;

    const slugsFromRoute = shouldAppendRouteSlugs
      ? route.params[slugParamName]
      : null;

    if (!slugsFromRoute) {
      return contentFolder;
    }

    if (typeof slugsFromRoute === "string") {
      return `${contentFolder}/${slugsFromRoute}` as const;
    }

    return `${contentFolder}/${slugsFromRoute.join("/")}` as const;
  });

  const isInPreviewMode = useIsInStoryblokPreviewMode();
  const { locale } = useI18n();

  const params = computed<ISbStoriesParamsGeneric<C>>(() => ({
    ...baseParams,
    version: isInPreviewMode.value ? "draft" : "published",
    language: locale.value,
    fallback_lang:
      platformStore.getPlatformConfiguration.properties.defaultLanguage
        .isoLanguageCode,
  }));

  return {
    slug,
    params,
    isInPreviewMode,
  };
};

export default useStoryblokRequestConfig;
